import Swal, { SweetAlertIcon, SweetAlertOptions, SweetAlertResult } from "sweetalert2";
import { Txt } from "./text";
import { Helper } from "./helper";

export enum MessageType {
    success = "Success",
    warning = "Warning",
    error = "Error",
    info = "Info",
    dialog = "Dialog"
}

export interface Message {
    Text: string;
    Type: MessageType;
}

export interface DialogMessage {
    Title: string;
    Text: string;
    ShowSuccessIcon: boolean;
    IsTextHtml: boolean;
    NewFormUrl?: string;
}

export class MessageDisplayer
{
    static fireAlert(options: SweetAlertOptions): Promise<SweetAlertResult>
    {
        const openDialogs = document.querySelectorAll<HTMLDialogElement>("dialog.w-dialog[open]");
        const target = openDialogs.length ? openDialogs[openDialogs.length - 1] : undefined;
        if (target && options.target == null)
        {
            options = { ...options, target };
        }
        return Swal.fire(options);
    }

    static showErrorFromServer(error)
    {
        if (error.status === 0)
        {
            MessageDisplayer.fireAlert({
                title: Txt.networkError,
                imageUrl: "images/error.png"
            });
        }
        else if (error.status === 400)
        {
            MessageDisplayer.showError(error.responseText);
        }
        else if (error.status === 401)
        {
            MessageDisplayer.fireAlert({
                text: Txt.sessionHasExpired,
                imageUrl: "images/error.png",
                confirmButtonText: Txt.logInAgain
            }).then(result =>
            {
                if (result.isConfirmed)
                {
                    Helper.refreshPage();
                }
            });
        }
        else
        {
            this.showGenericError();
        }
    }

    static showGenericError()
    {
        MessageDisplayer.fireAlert({
            text: Txt.someError,
            imageUrl: "images/error.png"
        });
    }

    static showDialog(dialog: DialogMessage): void
    {
        const options: SweetAlertOptions = {};
        if (dialog.IsTextHtml)
        {
            options.html = Helper.sanitizeHtml(dialog.Text);
        }
        else
        {
            options.text = dialog.Text;
        }
        if (dialog.Title)
        {
            options.title = dialog.Title;
        }
        if (dialog.ShowSuccessIcon)
        {
            options.icon = "success";
        }
        MessageDisplayer.fireAlert(options);
    }

    static showSuccess(message: string): void
    {
        this.showMessage({ Text: message, Type: MessageType.success });
    }

    static showWarning(message: string): void
    {
        this.showMessage({ Text: message, Type: MessageType.warning });
    }

    static showError(message: string): void
    {
        this.showMessage({ Text: message, Type: MessageType.error });
    }

    static showMessage(message: Message): void
    {
        if (message.Type === MessageType.dialog)
        {
            MessageDisplayer.fireAlert({ text: message.Text, allowOutsideClick: false });
            return;
        }
        let icon: SweetAlertIcon;
        let color: string;
        let background: string;
        let delay = 5000;
        if (message.Type === MessageType.success)
        {
            icon = "success";
            background = "#dff0d8";
            color = "#3c763d";
            delay = 2000;
        }
        else if (message.Type === MessageType.warning)
        {
            icon = "warning";
            background = "#fcf8e3";
            color = "#8a6d3b";
        }
        else if (message.Type === MessageType.error)
        {
            icon = "error";
            background = "#f2dede";
            color = "#a94442";
        }
        else if (message.Type === MessageType.info)
        {
            icon = "info";
            background = "#d9edf7";
            color = "#31708f";
        }
        else
        {
            throw new Error("Unexpected message type");
        }
        MessageDisplayer.fireAlert({
            toast: true,
            position: "top-start",
            icon: icon,
            title: message.Text,
            background: background,
            color: color,
            showConfirmButton: false,
            timer: delay,
            timerProgressBar: true,
            customClass: { popup: `w-toast w-toast-${message.Type.toLowerCase()}` }
        });
    }
}
